import { useState, useContext, useEffect, useCallback } from 'react'
import { Button } from '@/components/ui/shadcn/button'
import { Label } from '@/components/ui/shadcn/label'
import { Alert, AlertDescription } from '@/components/ui/shadcn/alert'
import { Switch } from '@/components/ui/shadcn/switch'
import { Checkbox } from '@/components/ui/shadcn/checkbox'
import { Badge } from '@/components/ui/shadcn/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/shadcn/select'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/shadcn/popover'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/shadcn/table'
import { CheckCircle2, Loader2, Plus, Trash2, ChevronDown } from 'lucide-react'
import { toast } from 'sonner'
import { AuthContext } from '@/contexts/AuthContext'
import { fetcher } from '@/lib/api/fetch'

// Valid ServiceChangeTypes (must match backend model.ServiceChangeType).
const CHANGE_TYPES = [
  { value: 'ai_model_change', label: 'AI Model Change' },
  { value: 'architecture_change', label: 'Architecture Change' },
  { value: 'subcontractor_change', label: 'Subcontractor Change' },
  { value: 'tier_change', label: 'Tier Change' },
  { value: 'vendor', label: 'Vendor' },
  { value: 'system', label: 'System' },
  { value: 'team', label: 'Team' },
  { value: 'policy', label: 'Policy' },
]

const EVIDENCE_CLASSES = [
  { value: 'test', label: 'Test' },
  { value: 'change', label: 'Change' },
  { value: 'operational', label: 'Operational' },
  { value: 'review', label: 'Review' },
  { value: 'governance', label: 'Governance' },
]

const SEVERITIES = [
  { value: 'informational', label: 'Informational' },
  { value: 'minor', label: 'Minor' },
  { value: 'material', label: 'Material' },
  { value: 'critical', label: 'Critical' },
]

const changeTypeLabel = value =>
  CHANGE_TYPES.find(t => t.value === value)?.label || value

/**
 * InvalidationRulesPanel — customer-facing settings panel for change-triggered
 * evidence invalidation rules. Each rule maps a service change type to the
 * evidence classes it challenges, a severity, and an enabled flag.
 *
 * Per CLAUDE.md placement rule: customer-facing → Settings, not Admin.
 * Mirrors the AssumptionFreshnessPanel API-call pattern (fetcher +
 * /api/admin/organization/:id/settings/...).
 */
export default function InvalidationRulesPanel() {
  const authContext = useContext(AuthContext)
  const org = authContext.user?.organization

  const [rules, setRules] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [error, setError] = useState(null)

  const loadRules = useCallback(async () => {
    if (!org?.id) return
    setLoading(true)
    setError(null)
    try {
      const res = await fetcher(
        `/api/admin/organization/${org.id}/settings/invalidation-rules`,
        { method: 'GET' },
        authContext,
      )
      const data = await res.json()
      if (!res.ok) {
        setError(data.error || 'Failed to load invalidation rules')
        return
      }
      setRules(Array.isArray(data.rules) ? data.rules : [])
    } catch (e) {
      setError(e.message || 'Failed to load invalidation rules')
    } finally {
      setLoading(false)
    }
  }, [org?.id, authContext])

  useEffect(() => {
    loadRules()
  }, [loadRules])

  const usedChangeTypes = new Set(rules.map(r => r.changeType))
  const availableChangeTypes = CHANGE_TYPES.filter(
    t => !usedChangeTypes.has(t.value),
  )

  const addRule = () => {
    const next = availableChangeTypes[0]
    if (!next) return
    setRules([
      ...rules,
      {
        changeType: next.value,
        challengesEvidenceClasses: [],
        severity: 'material',
        enabled: true,
      },
    ])
  }

  const updateRule = (index, patch) => {
    setRules(rules.map((r, i) => (i === index ? { ...r, ...patch } : r)))
  }

  const removeRule = index => {
    setRules(rules.filter((_, i) => i !== index))
  }

  const toggleEvidenceClass = (index, classValue) => {
    const rule = rules[index]
    const has = rule.challengesEvidenceClasses?.includes(classValue)
    const nextClasses = has
      ? rule.challengesEvidenceClasses.filter(c => c !== classValue)
      : [...(rule.challengesEvidenceClasses || []), classValue]
    updateRule(index, { challengesEvidenceClasses: nextClasses })
  }

  const handleSave = async () => {
    setSaving(true)
    setError(null)
    try {
      const res = await fetcher(
        `/api/admin/organization/${org.id}/settings/invalidation-rules`,
        {
          method: 'PUT',
          body: JSON.stringify({ rules }),
        },
        authContext,
      )
      const data = await res.json()
      if (!res.ok) {
        setError(data.error || 'Failed to save invalidation rules')
        return
      }
      if (Array.isArray(data.rules)) {
        setRules(data.rules)
      }
      setSaved(true)
      setTimeout(() => setSaved(false), 10000)
      toast.success('Invalidation rules saved')
    } catch (e) {
      setError(e.message || 'Failed to save invalidation rules')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="mb-4">
        <h3 className="text-sm font-semibold">Change-Triggered Invalidation</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          Define which classes of evidence are challenged when a service change
          of a given type is logged. Challenged evidence is re-validated before
          it counts towards readiness.
        </p>
      </div>

      {saved && (
        <Alert className="mb-3 border-emerald-200 bg-emerald-50 text-emerald-900 dark:border-emerald-900 dark:bg-emerald-950 dark:text-emerald-200">
          <CheckCircle2 className="size-4" />
          <AlertDescription>Invalidation rules saved</AlertDescription>
        </Alert>
      )}

      {error && (
        <Alert variant="destructive" className="mb-3">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="size-5 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[200px]">Change Type</TableHead>
                  <TableHead>Challenged Evidence Classes</TableHead>
                  <TableHead className="w-[160px]">Severity</TableHead>
                  <TableHead className="w-[90px]">Enabled</TableHead>
                  <TableHead className="w-[56px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rules.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={5}
                      className="py-6 text-center text-sm text-muted-foreground"
                    >
                      No invalidation rules. Add one to get started.
                    </TableCell>
                  </TableRow>
                ) : (
                  rules.map((rule, index) => (
                    <TableRow key={rule.changeType}>
                      <TableCell className="align-top font-medium">
                        {changeTypeLabel(rule.changeType)}
                      </TableCell>
                      <TableCell className="align-top whitespace-normal">
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-8 w-full justify-between font-normal"
                            >
                              <span className="truncate text-left">
                                {rule.challengesEvidenceClasses?.length
                                  ? `${rule.challengesEvidenceClasses.length} selected`
                                  : 'Select classes'}
                              </span>
                              <ChevronDown className="size-4 opacity-50" />
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent align="start" className="w-56 p-2">
                            <div className="flex flex-col gap-1">
                              {EVIDENCE_CLASSES.map(ec => {
                                const id = `ec-${index}-${ec.value}`
                                const checked =
                                  rule.challengesEvidenceClasses?.includes(
                                    ec.value,
                                  ) || false
                                return (
                                  <label
                                    key={ec.value}
                                    htmlFor={id}
                                    className="flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
                                  >
                                    <Checkbox
                                      id={id}
                                      checked={checked}
                                      onCheckedChange={() =>
                                        toggleEvidenceClass(index, ec.value)
                                      }
                                    />
                                    {ec.label}
                                  </label>
                                )
                              })}
                            </div>
                          </PopoverContent>
                        </Popover>
                        {rule.challengesEvidenceClasses?.length > 0 && (
                          <div className="mt-1.5 flex flex-wrap gap-1">
                            {rule.challengesEvidenceClasses.map(c => (
                              <Badge
                                key={c}
                                variant="secondary"
                                className="text-[10px]"
                              >
                                {EVIDENCE_CLASSES.find(e => e.value === c)
                                  ?.label || c}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="align-top">
                        <Select
                          value={rule.severity}
                          onValueChange={v =>
                            updateRule(index, { severity: v })
                          }
                        >
                          <SelectTrigger size="sm" className="w-full">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {SEVERITIES.map(s => (
                              <SelectItem key={s.value} value={s.value}>
                                {s.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="align-top">
                        <Switch
                          checked={!!rule.enabled}
                          onCheckedChange={v =>
                            updateRule(index, { enabled: v })
                          }
                          aria-label={`Enable ${changeTypeLabel(rule.changeType)} rule`}
                        />
                      </TableCell>
                      <TableCell className="align-top">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="size-8 text-muted-foreground hover:text-destructive"
                          onClick={() => removeRule(index)}
                          aria-label={`Remove ${changeTypeLabel(rule.changeType)} rule`}
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          <div className="mt-4 flex items-center gap-2">
            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <>
                  <Loader2 className="mr-1 size-3.5 animate-spin" />
                  Saving…
                </>
              ) : (
                'Save'
              )}
            </Button>
            <Button
              variant="outline"
              onClick={addRule}
              disabled={availableChangeTypes.length === 0}
            >
              <Plus className="size-4" />
              Add rule
            </Button>
            {availableChangeTypes.length === 0 && (
              <Label className="text-xs font-normal text-muted-foreground">
                All change types have a rule.
              </Label>
            )}
          </div>
        </>
      )}
    </div>
  )
}
